import { useState } from 'react';
import { MamaApprovedBusiness } from '../../../mocks/mamaApproved';

interface Props {
  business: MamaApprovedBusiness;
  onClose: () => void;
}

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const iconSize = size === 'md' ? 'text-base' : 'text-xs';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <div key={star} className={`w-4 h-4 flex items-center justify-center`}>
          <i
            className={`${iconSize} ${
              star <= Math.floor(rating)
                ? 'ri-star-fill text-amber-400'
                : star - 0.5 <= rating
                ? 'ri-star-half-fill text-amber-400'
                : 'ri-star-line text-gray-300'
            }`}
          />
        </div>
      ))}
    </div>
  );
}

export default function BusinessDetailModal({ business, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<'about' | 'reviews'>('about');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50 px-8 pt-8 pb-6">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center bg-white rounded-full shadow-sm hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-600 text-lg"></i>
          </button>

          <div className="flex items-start gap-5">
            <div className="relative shrink-0">
              <img
                src={business.logo}
                alt={business.name}
                className="w-20 h-20 rounded-2xl object-cover shadow-md"
              />
              <div className="absolute -bottom-2 -right-2 w-7 h-7 flex items-center justify-center bg-amber-400 rounded-full shadow-sm">
                <i className="ri-medal-fill text-gray-900 text-sm"></i>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2.5 py-0.5 bg-pink-100 text-pink-600 text-xs font-semibold rounded-full">
                  {business.category}
                </span>
                <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full flex items-center gap-1">
                  <i className="ri-medal-fill text-xs"></i>
                  Mama Approved
                </span>
              </div>
              <h2 className="text-xl font-extrabold text-gray-900 leading-tight mb-2">{business.name}</h2>
              <div className="flex items-center gap-2">
                <StarRating rating={business.averageRating} size="md" />
                <span className="text-sm font-bold text-gray-800">{business.averageRating.toFixed(1)}</span>
                <span className="text-sm text-gray-400">({business.reviewCount} mom reviews)</span>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mt-4">
            {business.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 bg-white/80 text-gray-600 text-xs font-medium rounded-full border border-gray-200">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Member Discount Banner */}
        <div className="mx-6 mt-4 flex items-center gap-3 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-2xl">
          <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
            <i className="ri-gift-2-line text-emerald-600 text-lg"></i>
          </div>
          <div>
            <p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Mommy Mingle Member Discount</p>
            <p className="text-sm font-semibold text-emerald-700">{business.memberDiscount}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mx-6 mt-5 bg-gray-100 p-1 rounded-full">
          <button
            onClick={() => setActiveTab('about')}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'about' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            About
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`flex-1 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
              activeTab === 'reviews' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Mom Reviews ({business.reviewCount})
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === 'about' && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">About This Business</h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">{business.description}</p>

              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-shield-check-fill text-amber-500 text-lg"></i>
                  </div>
                  <h4 className="text-sm font-bold text-amber-800">Why Moms Trust This Business</h4>
                </div>
                <p className="text-xs text-amber-700 leading-relaxed">
                  This business has earned the <strong>Mama Approved</strong> badge by consistently receiving top ratings and glowing reviews from moms in the Mommy Mingle community. It has been personally vetted and recommended by local moms in Gwinnett County and surrounding areas.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'reviews' && (
            <div className="space-y-4">
              {business.reviews.map((review, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-pink-100 rounded-full shrink-0">
                      <span className="text-pink-600 text-sm font-bold">{review.reviewerName.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-gray-900">{review.reviewerName}</span>
                        <span className="text-xs text-gray-400">
                          {new Date(review.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                      <StarRating rating={review.rating} />
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">{review.comment}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          <button
            onClick={onClose}
            className="w-full py-3 bg-pink-600 text-white text-sm font-bold rounded-xl hover:bg-pink-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
